'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography, useTheme } from '@mui/material';
import { Bar } from 'react-chartjs-2';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Tooltip, Legend } from 'chart.js';
ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);
const CapacityVsRegisteredBarChart = ({ title, data, capacityLabel = 'Capacity', registeredLabel = 'Registered', height = 280, fillParent, yMin = 0, yMax = 100, yStep = 20 }) => {
    const theme = useTheme();
    const TOOLTIP_LINE_LENGTH = 32;
    const wrapText = (text, maxLineLength) => {
        const words = String(text || '')
            .split(/\s+/)
            .filter(Boolean);
        if (!words.length)
            return [''];
        const lines = [];
        let currentLine = '';
        words.forEach((word) => {
            const next = currentLine ? `${currentLine} ${word}` : word;
            if (next.length <= maxLineLength) {
                currentLine = next;
                return;
            }
            if (currentLine)
                lines.push(currentLine);
            if (word.length > maxLineLength) {
                for (let i = 0; i < word.length; i += maxLineLength) {
                    lines.push(word.slice(i, i + maxLineLength));
                }
                currentLine = '';
            }
            else {
                currentLine = word;
            }
        });
        if (currentLine)
            lines.push(currentLine);
        return lines;
    };
    const lighterBlue = theme.palette.primary?.light ?? '#82B1FF';
    const darkerBlue = theme.palette.primary?.main ?? '#1976d2';
    const getOrCreateTooltipEl = React.useCallback(() => {
        const tooltipStyleId = 'capacity-vs-registered-tooltip-style';
        if (!document.getElementById(tooltipStyleId)) {
            const styleEl = document.createElement('style');
            styleEl.id = tooltipStyleId;
            styleEl.innerHTML = `
        #capacity-vs-registered-tooltip::-webkit-scrollbar { width: 6px; }
        #capacity-vs-registered-tooltip::-webkit-scrollbar-track { background: transparent; }
        #capacity-vs-registered-tooltip::-webkit-scrollbar-thumb { background-color: ${theme.palette.grey[500]}; border-radius: 8px; }
      `;
            document.head.appendChild(styleEl);
        }
        let tooltipEl = document.getElementById('capacity-vs-registered-tooltip');
        if (!tooltipEl) {
            tooltipEl = document.createElement('div');
            tooltipEl.id = 'capacity-vs-registered-tooltip';
            tooltipEl.style.position = 'fixed';
            tooltipEl.style.pointerEvents = 'auto';
            tooltipEl.style.transform = 'translate(-50%, 0)';
            tooltipEl.style.background = theme.palette.grey[800];
            tooltipEl.style.color = theme.palette.common.white;
            tooltipEl.style.borderRadius = '8px';
            tooltipEl.style.padding = '10px';
            tooltipEl.style.fontSize = '12px';
            tooltipEl.style.lineHeight = '1.4';
            tooltipEl.style.opacity = '0';
            tooltipEl.style.transition = 'opacity 120ms ease';
            tooltipEl.style.zIndex = '9999';
            tooltipEl.style.maxWidth = '320px';
            tooltipEl.style.maxHeight = '200px';
            tooltipEl.style.overflowY = 'auto';
            tooltipEl.style.overflowX = 'hidden';
            tooltipEl.style.whiteSpace = 'normal';
            tooltipEl.style.setProperty('scrollbar-width', 'thin');
            tooltipEl.style.setProperty('scrollbar-color', `${theme.palette.grey[500]} transparent`);
            tooltipEl.style.paddingRight = '10px';
            tooltipEl.style.boxSizing = 'border-box';
            tooltipEl.dataset.sticky = 'false';
            tooltipEl.addEventListener('mouseenter', () => {
                tooltipEl.dataset.sticky = 'true';
            });
            tooltipEl.addEventListener('mouseleave', () => {
                tooltipEl.dataset.sticky = 'false';
                tooltipEl.style.opacity = '0';
            });
            document.body.appendChild(tooltipEl);
        }
        return tooltipEl;
    }, [theme]);
    const chartData = useMemo(() => ({
        labels: data.map((d) => d.label),
        datasets: [
            {
                label: capacityLabel,
                data: data.map((d) => d.capacity),
                backgroundColor: lighterBlue,
                borderColor: lighterBlue,
                borderWidth: 0,
                barPercentage: 0.6,
                categoryPercentage: 0.8
            },
            {
                label: registeredLabel,
                data: data.map((d) => d.registered),
                backgroundColor: darkerBlue,
                borderColor: darkerBlue,
                borderWidth: 0,
                barPercentage: 0.6,
                categoryPercentage: 0.8
            }
        ]
    }), [data, capacityLabel, registeredLabel, lighterBlue, darkerBlue]);
    const MIN_BAR_GROUP_WIDTH = 120;
    const chartWidth = Math.max(data.length * MIN_BAR_GROUP_WIDTH, 480);
    const scrollContainerRef = useRef(null);
    const [containerWidth, setContainerWidth] = useState(0);
    useEffect(() => {
        const el = scrollContainerRef.current;
        if (!el)
            return;
        const ro = new ResizeObserver((entries) => {
            setContainerWidth(entries[0].contentRect.width);
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);
    const CHAR_WIDTH_PX = 6.5;
    const maxLabelChars = useMemo(() => {
        const effectiveWidth = Math.max(containerWidth, chartWidth);
        const perBar = effectiveWidth / (data.length || 1) - 8;
        return Math.max(3, Math.floor(perBar / CHAR_WIDTH_PX));
    }, [containerWidth, chartWidth, data.length]);
    const options = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        // Hover ("active" element) transitions animate by default in Chart.js, separately from
        // the initial draw-in animation. Disabling just this keeps hover response instant without
        // affecting the load-in animation.
        transitions: {
            active: {
                animation: { duration: 0 }
            }
        },
        interaction: {
            mode: 'index',
            intersect: false
        },
        elements: {
            bar: {
                borderRadius: {
                    topLeft: 12,
                    topRight: 12,
                    bottomLeft: 24,
                    bottomRight: 24
                }
            }
        },
        plugins: {
            legend: {
                display: true,
                position: 'bottom',
                labels: {
                    usePointStyle: true,
                    pointStyle: 'rect',
                    padding: 16,
                    color: theme.palette.text.primary,
                    font: { size: 12 }
                }
            },
            tooltip: {
                enabled: false,
                mode: 'index',
                intersect: false,
                external: (context) => {
                    const { chart, tooltip } = context;
                    const tooltipEl = getOrCreateTooltipEl();
                    if (tooltip.opacity === 0) {
                        if (tooltipEl.dataset.sticky === 'true')
                            return;
                        tooltipEl.style.opacity = '0';
                        return;
                    }
                    const titleLines = tooltip.title || [];
                    const bodyLines = tooltip.body?.flatMap((b) => b.lines) || [];
                    const afterBodyLines = tooltip.afterBody || [];
                    const html = [
                        ...titleLines.map((line) => `<div style="font-weight:600; margin-bottom:4px;">${line}</div>`),
                        ...bodyLines.map((line) => `<div>${line}</div>`),
                        ...afterBodyLines.map((line) => `<div>${line}</div>`)
                    ].join('');
                    tooltipEl.innerHTML = html;
                    const canvasRect = chart.canvas.getBoundingClientRect();
                    tooltipEl.style.opacity = '1';
                    tooltipEl.style.left = `${canvasRect.left + tooltip.caretX}px`;
                    tooltipEl.style.top = `${canvasRect.top + tooltip.caretY + 8}px`;
                },
                callbacks: {
                    title(tooltipItems) {
                        if (!tooltipItems.length)
                            return [];
                        const i = tooltipItems[0].dataIndex;
                        const d = data[i];
                        const lines = [...wrapText(`Location: ${d.label}`, TOOLTIP_LINE_LENGTH)];
                        if (d.pickupPoint) {
                            lines.push(...wrapText(`Pickup Point: ${d.pickupPoint}`, TOOLTIP_LINE_LENGTH));
                        }
                        return lines;
                    },
                    label() {
                        return '';
                    },
                    afterBody(tooltipItems) {
                        if (!tooltipItems.length)
                            return [];
                        const i = tooltipItems[0].dataIndex;
                        const d = data[i];
                        return [
                            '• ' + capacityLabel + ': ' + d.capacity,
                            '• ' + registeredLabel + ': ' + d.registered
                        ];
                    }
                }
            }
        },
        scales: {
            x: {
                barPercentage: 0.6,
                categoryPercentage: 0.8,
                grid: { display: false },
                ticks: {
                    display: true,
                    color: theme.palette.text.secondary,
                    font: { size: 11 },
                    autoSkip: false,
                    maxRotation: 0,
                    minRotation: 0,
                    callback(value) {
                        const rawLabel = String(this.getLabelForValue(Number(value)) ?? '');
                        if (rawLabel.length <= maxLabelChars)
                            return rawLabel;
                        return `${rawLabel.slice(0, maxLabelChars - 1)}...`;
                    }
                },
                border: { display: false }
            },
            y: {
                min: yMin,
                max: yMax,
                grid: {
                    display: true,
                    color: theme.palette.divider
                },
                ticks: {
                    stepSize: yStep,
                    color: theme.palette.text.secondary,
                    font: { size: 11 }
                },
                border: { display: false }
            }
        }
    }), [
        theme,
        data,
        capacityLabel,
        registeredLabel,
        maxLabelChars,
        getOrCreateTooltipEl,
        yMin,
        yMax,
        yStep
    ]);
    return (_jsxs(Box, { sx: { display: 'flex', flexDirection: 'column', height: '100%' }, children: [title && (_jsx(Typography, { variant: 'h6', sx: { fontWeight: 500, color: theme.palette.text.primary, mb: 2 }, children: title })), _jsx(Box, { ref: scrollContainerRef, sx: {
                    flex: 1,
                    minHeight: 0,
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    pb: 1,
                    '&::-webkit-scrollbar': { height: 6 },
                    '&::-webkit-scrollbar-track': { background: 'transparent' },
                    '&::-webkit-scrollbar-thumb': {
                        backgroundColor: theme.palette.grey[400],
                        borderRadius: 8
                    }
                }, children: _jsx(Box, { sx: {
                        height: fillParent ? '100%' : height,
                        minWidth: chartWidth,
                        width: '100%',
                        minHeight: fillParent ? 260 : height,
                        flexShrink: 0
                    }, children: _jsx(Bar, { data: chartData, options: options }) }) })] }));
};
export default CapacityVsRegisteredBarChart;
